import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { AlertCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';

const deleteAccountSchema = z.object({
  confirmDelete: z.literal(true, {
    invalid_type_error: 'You must confirm this action',
  }),
});

const deleteOrgSchema = z.object({
  organizationName: z.string().min(1, 'Please enter the organization name'),
  confirmDeleteOrg: z.literal(true, {
    invalid_type_error: 'You must confirm this action',
  }),
});

type DeleteAccountFormValues = z.infer<typeof deleteAccountSchema>;
type DeleteOrgFormValues = z.infer<typeof deleteOrgSchema>;

interface OrganizationInfo {
  name: string;
  organization_id: string;
  isOnlyAdmin: boolean;
  isOnlyMember: boolean;
  userRole: 'admin' | 'editor' | 'viewer';
}

export function DeleteAccountForm() {
  const { user, deleteAccount } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizationInfo, setOrganizationInfo] = useState<OrganizationInfo | null>(null);
  const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
  const navigate = useNavigate();
  
  // Form for account deletion
  const accountForm = useForm<DeleteAccountFormValues>({
    resolver: zodResolver(deleteAccountSchema),
    defaultValues: {
      confirmDelete: true,
    },
  });

  // Form for organization deletion
  const orgForm = useForm<DeleteOrgFormValues>({
    resolver: zodResolver(deleteOrgSchema),
    defaultValues: {
      organizationName: '',
      confirmDeleteOrg: true,
    },
  });

  // Fetch organization info when component mounts
  useEffect(() => {
    const fetchOrgInfo = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Get user's organization and role
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('organization_id, role')
          .eq('user_id', user.id)
          .single();
          
        if (userError) throw userError;
        
        if (!userData.organization_id) {
          // User doesn't have an organization
          setOrganizationInfo(null);
          return;
        }
        
        // Get organization details
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('name')
          .eq('organization_id', userData.organization_id)
          .single();
          
        if (orgError) throw orgError;

        // Check if user is the only admin
        const { data: otherAdmins, error: adminError } = await supabase
          .from('users')
          .select('user_id')
          .eq('organization_id', userData.organization_id)
          .eq('role', 'admin')
          .neq('user_id', user.id);
          
        if (adminError) throw adminError;
        
        // Check if user is the only member
        const { data: otherMembers, error: memberError } = await supabase
          .from('users')
          .select('user_id')
          .eq('organization_id', userData.organization_id)
          .neq('user_id', user.id);
          
        if (memberError) throw memberError;
        
        setOrganizationInfo({
          name: orgData.name,
          organization_id: userData.organization_id,
          isOnlyAdmin: userData.role === 'admin' && otherAdmins.length === 0,
          isOnlyMember: otherMembers.length === 0,
          userRole: userData.role,
        });
      } catch (error: any) {
        console.error('Error fetching organization info:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrgInfo();
  }, [user]);

  const onDeleteAccount = async () => {
    if (!user) {
      toast.error('You must be logged in to delete your account');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // If the user is the only admin (but not only member), they need to assign new admin
      if (organizationInfo?.isOnlyAdmin && !organizationInfo.isOnlyMember) {
        setError('You are the only admin of your organization. Please assign another admin before deleting your account.');
        return;
      }
      
      // If sole member of org, delete both
      if (organizationInfo?.isOnlyMember) {
        // Delete account and organization
        const { error: deleteError } = await deleteAccount(true);
        if (deleteError) throw deleteError;
        
        toast.success('Your account and organization have been deleted.');
        navigate('/auth/login');
        return;
      }
      
      // If admin, ask if they want to delete the organization too
      if (organizationInfo?.userRole === 'admin') {
        setShowDeleteOrgDialog(true);
        return;
      }
      
      // Just delete the account, organization remains
      const { error: deleteError } = await deleteAccount();
      if (deleteError) throw deleteError;
      
      toast.success('Your account has been deleted.');
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setError(error.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const onDeleteOrganization = async (data: DeleteOrgFormValues) => {
    if (!organizationInfo) return;
    
    try {
      setLoading(true);
      setError(null);
      
      if (data.organizationName !== organizationInfo.name) {
        setError('Organization name does not match');
        return;
      }
      
      // Delete account and organization
      const { error: deleteError } = await deleteAccount(true, data.organizationName);
      if (deleteError) throw deleteError;
      
      toast.success('Your account and organization have been deleted.');
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Error deleting organization:', error);
      setError(error.message || 'Failed to delete organization');
    } finally {
      setLoading(false);
      setShowDeleteOrgDialog(false);
    }
  };

  const handleKeepOrgDeleteAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Just delete the account, organization remains
      const { error: deleteError } = await deleteAccount();
      if (deleteError) throw deleteError;
      
      toast.success('Your account has been deleted.');
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setError(error.message || 'Failed to delete account');
    } finally {
      setLoading(false);
      setShowDeleteOrgDialog(false);
    }
  };

  return (
    <>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-destructive">Delete Account</CardTitle>
          <CardDescription className="text-center">
            This action is permanent and cannot be undone
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {organizationInfo && (
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {organizationInfo.isOnlyMember 
                  ? `As the only member of ${organizationInfo.name}, the organization will also be deleted.`
                  : organizationInfo.isOnlyAdmin && !organizationInfo.isOnlyMember
                    ? 'You are the only admin. Please assign another admin before deleting your account.'
                    : `You are a member of ${organizationInfo.name}.`}
              </AlertDescription>
            </Alert>
          )}
          
          <Form {...accountForm}>
            <form onSubmit={accountForm.handleSubmit(() => onDeleteAccount())} className="space-y-4">
              <FormField
                control={accountForm.control}
                name="confirmDelete"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I understand that this will permanently delete my account
                      </FormLabel>
                      <FormDescription>
                        All your data will be permanently removed
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <Button type="submit" variant="destructive" className="w-full" disabled={loading || (organizationInfo?.isOnlyAdmin && !organizationInfo.isOnlyMember)}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Delete Account
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => navigate('/settings')}
          >
            Cancel
          </Button>
        </CardFooter>
      </Card>
      
      {/* Delete Organization Dialog */}
      <Dialog open={showDeleteOrgDialog} onOpenChange={setShowDeleteOrgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization?</DialogTitle>
            <DialogDescription>
              Would you like to delete the organization "{organizationInfo?.name}" as well?
              This will remove all data and users from the organization.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...orgForm}>
            <form onSubmit={orgForm.handleSubmit(onDeleteOrganization)} className="space-y-4">
              <FormField
                control={orgForm.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Organization Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter organization name" {...field} />
                    </FormControl>
                    <FormDescription>
                      Type "{organizationInfo?.name}" to confirm deletion
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={orgForm.control}
                name="confirmDeleteOrg"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I understand this will permanently delete the organization
                      </FormLabel>
                      <FormDescription>
                        All organization data and user access will be permanently removed
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <DialogFooter className="flex sm:justify-between">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleKeepOrgDeleteAccount}
                  disabled={loading}
                >
                  Just Delete My Account
                </Button>
                <Button 
                  type="submit" 
                  variant="destructive"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Delete Account & Organization
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
} 